import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MarketplaceFilters, { FilterState } from "./components/MarketplaceFilters";
import { mockProducts, mockCategories } from "@/mocks/products";
import SEO from "@/components/base/SEO";

const FACTORY_COMMISSION: Record<string, number> = {
  "TechVision Factory": 10,
  "PureGlow Labs": 15,
  "FitForge Manufacturing": 8,
  "GreenThread Mills": 12,
  "LightCraft Industries": 9,
  "HydroForge Co.": 7,
  "SoundWave Factory": 11,
  "EcoKitchen Works": 6,
};

const FACTORY_WAREHOUSE: Record<string, string[]> = {
  "TechVision Factory": ["USA Local", "Overseas"],
  "PureGlow Labs": ["USA Local", "Overseas"],
  "FitForge Manufacturing": ["USA Local", "Overseas"],
  "GreenThread Mills": ["USA Local"],
  "LightCraft Industries": ["USA Local"],
  "HydroForge Co.": ["USA Local"],
  "SoundWave Factory": ["USA Local", "Overseas"],
  "EcoKitchen Works": ["USA Local"],
};

const FACTORY_ID: Record<string, string> = {
  "TechVision Factory": "techvision",
  "PureGlow Labs": "pureglow",
  "FitForge Manufacturing": "fitforge",
};

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const marketplaceSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "USA Local Marketplace — StarBookPrime",
  "description": "Browse and buy products directly from verified USA local factories. Wholesale pricing, reseller commissions, and fast domestic shipping.",
  "url": `${SITE_URL}/marketplace`,
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
      { "@type": "ListItem", "position": 2, "name": "Marketplace", "item": `${SITE_URL}/marketplace` }
    ]
  }
};

export default function MarketplacePage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [wishlist, setWishlist] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterState>({
    search: "",
    category: "all",
    factory: "All Factories",
    commissionMin: 0,
    commissionMax: 100,
    priceMin: "",
    priceMax: "",
    warehouse: "All Locations",
    sortBy: "trending",
  });

  const toggleWishlist = (id: string) => {
    setWishlist((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  };

  const filtered = useMemo(() => {
    let result = activeCategory === "all"
      ? mockProducts
      : mockProducts.filter((p) => p.category.toLowerCase() === activeCategory);

    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter((p) =>
        p.title.toLowerCase().includes(q) ||
        p.seller.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
      );
    }

    if (filters.factory !== "All Factories") {
      result = result.filter((p) => p.seller === filters.factory);
    }

    if (filters.warehouse !== "All Locations") {
      result = result.filter((p) => {
        const wh = FACTORY_WAREHOUSE[p.seller] || [];
        return wh.includes(filters.warehouse);
      });
    }

    if (filters.commissionMin > 0) {
      result = result.filter((p) => (FACTORY_COMMISSION[p.seller] || 0) >= filters.commissionMin);
    }

    if (filters.priceMin !== "") {
      result = result.filter((p) => p.price >= Number(filters.priceMin));
    }
    if (filters.priceMax !== "") {
      result = result.filter((p) => p.price <= Number(filters.priceMax));
    }

    return [...result].sort((a, b) => {
      if (filters.sortBy === "price_low") return a.price - b.price;
      if (filters.sortBy === "price_high") return b.price - a.price;
      if (filters.sortBy === "rating") return b.rating - a.rating;
      if (filters.sortBy === "commission_high") {
        return (FACTORY_COMMISSION[b.seller] || 0) - (FACTORY_COMMISSION[a.seller] || 0);
      }
      return b.reviews - a.reviews;
    });
  }, [activeCategory, filters]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f5f5f5" }}>
      <SEO
        title="USA Local Marketplace — Shop Verified Factory Products | StarBookPrime"
        description="Browse 8,500+ products from verified USA local factories. Filter by commission rate, warehouse location, and price. Earn up to 15% reseller commission on every sale."
        keywords="USA local factory products, verified factory marketplace, reseller commission products"
        canonical="/marketplace"
        ogImage="https://public.readdy.ai/ai/img_res/634e7ff9c5eab745a1c1b336d0e32ad6.jpg"
        schema={marketplaceSchema}
      />
      <Navbar />
      <div className="pt-16">
        {/* Banner */}
        <div className="relative h-48 md:h-64 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=USA%20local%20marketplace%20shopping%20colorful%20products%20display%20vibrant%20commerce%20american%20flag%20theme%20warm%20orange%20tones&width=1440&height=400&seq=mktbanner&orientation=landscape"
            alt="Marketplace"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/20 flex items-center px-8 md:px-16">
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-2">USA Local Marketplace</h1>
              <p className="text-white/80 text-sm md:text-base">Shop directly from verified American factories</p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {mockCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.id
                    ? "bg-orange-500 text-white"
                    : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-500"
                }`}
              >
                <i className={cat.icon} />{cat.label}
              </button>
            ))}
          </div>

          {/* Live Search & Filters */}
          <MarketplaceFilters filters={filters} onChange={setFilters} resultCount={filtered.length} />

          {/* Product Grid */}
          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-full mx-auto mb-4">
                <i className="ri-search-line text-3xl text-gray-300" />
              </div>
              <p className="text-gray-500 font-medium">No products match your filters</p>
              <p className="text-xs text-gray-400 mt-1">Try adjusting your search or clearing filters</p>
              <button
                onClick={() => setFilters({ search: "", category: "all", factory: "All Factories", commissionMin: 0, commissionMax: 100, priceMin: "", priceMax: "", warehouse: "All Locations", sortBy: "trending" })}
                className="mt-4 px-5 py-2 bg-orange-500 text-white text-sm font-semibold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop>
              {filtered.map((product) => {
                const commission = FACTORY_COMMISSION[product.seller] || 0;
                const factoryId = FACTORY_ID[product.seller];
                return (
                  <div key={product.id} className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-orange-200 transition-all group cursor-pointer">
                    <div className="relative aspect-square overflow-hidden">
                      <img src={product.image} alt={product.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                      <div className="absolute top-2 left-2 flex flex-col gap-1">
                        {product.verified && (
                          <span className="flex items-center gap-1 bg-white/90 text-green-600 text-xs font-semibold px-2 py-1 rounded-full">
                            <i className="ri-shield-check-fill" /> Verified
                          </span>
                        )}
                        {product.badge && (
                          <span className="bg-orange-500 text-white text-xs font-semibold px-2 py-1 rounded-full">{product.badge}</span>
                        )}
                      </div>
                      {commission > 0 && (
                        <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs font-bold px-2 py-1 rounded-full">
                          {commission}% commission
                        </div>
                      )}
                      <button onClick={() => toggleWishlist(product.id)} className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-white/90 rounded-full cursor-pointer hover:bg-white">
                        <i className={wishlist.includes(product.id) ? "ri-heart-fill text-red-500" : "ri-heart-line text-gray-400"} />
                      </button>
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1">{product.title}</h3>
                      {factoryId ? (
                        <Link to={`/factory/${factoryId}`} className="text-xs text-orange-500 hover:underline mb-2 block cursor-pointer" onClick={(e) => e.stopPropagation()}>{product.seller}</Link>
                      ) : (
                        <p className="text-xs text-orange-500 mb-2">{product.seller}</p>
                      )}
                      <div className="flex items-center gap-1 mb-2">
                        <span className="text-yellow-400 text-xs">{"★".repeat(Math.floor(product.rating))}</span>
                        <span className="text-xs text-gray-400">({product.reviews.toLocaleString()})</span>
                      </div>
                      {/* Warehouse Tags */}
                      <div className="flex flex-wrap gap-1 mb-3">
                        {(FACTORY_WAREHOUSE[product.seller] || []).map((wh) => (
                          <span key={wh} className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${wh === "USA Local" ? "bg-green-50 text-green-600" : "bg-orange-50 text-orange-600"}`}>
                            {wh === "USA Local" ? "🇺🇸 Local" : "🌏 Overseas"}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg font-bold text-gray-900">${product.price}</span>
                        <span className="text-sm text-gray-400 line-through">${product.originalPrice}</span>
                        <span className="text-xs text-green-600 font-semibold">{Math.round((1 - product.price / product.originalPrice) * 100)}% off</span>
                      </div>
                      <Link
                        to="/cart"
                        className="w-full py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                      >
                        <i className="ri-shopping-cart-line" /> Add to Cart
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Overseas Buyer Quota Section */}
          <div className="mt-16 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12 text-white">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <span className="inline-block bg-orange-500 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">Overseas Buyers</span>
                <h2 className="text-2xl md:text-3xl font-bold mb-3">International Buyer Quota Program</h2>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  International buyers can access exclusive quota allocations from verified US factories. Bulk orders, competitive pricing, and direct factory relationships.
                </p>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {[
                    { value: "120+", label: "Countries" },
                    { value: "$500M+", label: "Export Volume" },
                    { value: "5,000+", label: "Factory Partners" },
                  ].map((s) => (
                    <div key={s.label}>
                      <div className="text-xl font-bold text-orange-400">{s.value}</div>
                      <div className="text-xs text-gray-400">{s.label}</div>
                    </div>
                  ))}
                </div>
                <button className="px-6 py-3 bg-orange-500 text-white font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
                  Apply for Buyer Quota
                </button>
              </div>
              <div className="w-full md:w-80 h-48 rounded-xl overflow-hidden flex-shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=international%20shipping%20containers%20port%20cargo%20global%20trade%20logistics%20aerial%20view%20colorful%20containers&width=640&height=400&seq=overseas1&orientation=landscape"
                  alt="Overseas Buyers"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
